import { Track } from "@/src/lib/tracking";
import { bigDecimal } from "js-big-decimal";
import { formatNumber } from "utils";
import { EarningsCalendar } from "@features/api/openData";

/**
 * 安全地将值转换为数字
 * 处理字符串和数字类型的输入
 * API 返回的数值字段都是字符串，需要转换
 */
function toNumber(value?: string | number | null): number | null {
  if (value == null) {
    return null;
  }
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? null : num;
}

/**
 * 使用 BigDecimal 进行精确计算
 * @param value - 输入值
 * @returns BigDecimal 字符串，如果无效则返回 null
 */
function toBigDecimal(value?: string | number | null): string | null {
  if (value == null) {
    return null;
  }
  const str = typeof value === "number" ? value.toString() : value;
  // 验证是否为有效数字
  if (!/^-?\d+\.?\d*$/.test(str)) {
    return null;
  }
  return str;
}

/**
 * 从带有单位（B/M/K）的字符串中提取并还原实际数值
 * @param valueStr - 带有单位的字符串（如 "1.5M", "152.98B", "500K"）
 * @returns 还原后的实际数值，无效值返回 null
 */
function extractValueWithUnit(valueStr: string): number | null {
  if (!valueStr) {
    return null;
  }

  // 移除单位，提取数字部分
  const cleanValue = valueStr.replace(/[BKM]$/, "");
  const num = parseFloat(cleanValue);

  if (isNaN(num)) {
    return null;
  }

  // 根据单位补零，还原实际数值
  if (valueStr.endsWith("B")) {
    return num * 1000000000; // 1B = 1,000,000,000
  } else if (valueStr.endsWith("M")) {
    return num * 1000000; // 1M = 1,000,000
  } else if (valueStr.endsWith("K")) {
    return num * 1000; // 1K = 1,000
  }

  return num;
}

/**
 * 计算 Surprise 百分比
 * @formula (actual - estimate) / estimate × 100
 * @param actual - 可以是字符串或数字（API 返回字符串）
 * @param estimate - 可以是字符串或数字（API 返回字符串）
 * @returns 使用 BigDecimal 进行精确计算，避免浮点数精度问题
 */
export function calculateSurprise(
  actual?: string | number | null,
  estimate?: string | number | null,
): number | null {
  const actualStr = toBigDecimal(actual);
  const estimateStr = toBigDecimal(estimate);

  if (!actualStr || !estimateStr || estimateStr === "0") {
    return null;
  }

  try {
    // 使用 BigDecimal 进行精确计算
    // (actual - estimate) / estimate * 100
    const actualBD = bigDecimal.divide(actualStr, "1", 10); // 保留10位小数精度
    const estimateBD = bigDecimal.divide(estimateStr, "1", 10);
    const diff = bigDecimal.subtract(actualBD, estimateBD);

    const ratio = bigDecimal.divide(diff, estimateBD, 10);
    const percentage = bigDecimal.multiply(ratio, "100");

    // eslint-disable-next-line logs-transform/prefer-log-over-console
    console.log(
      diff,
      "diffdiffdiff",
      ratio,
      estimateBD,
      percentage,
      actualStr,
      estimateStr,
      actual,
      estimate,
    );
    return parseFloat(percentage);
  } catch (error) {
    Track.log("error", "earnings", "Error calculating surprise:", {
      cause: String(error),
    });
    return null;
  }
}

/**
 * 从 formatEPSValue 返回的字符串中提取纯数字
 * 用于基于屏幕显示的格式化 EPS 值计算 Surprise
 * @param formattedEPS - formatEPSValue 返回的字符串（如 "$1.57" 或 "--"）
 * @returns 纯数字（如 1.57），无效值返回 null
 */
export function extractEPSFromFormatted(formattedEPS: string): number | null {
  if (!formattedEPS || formattedEPS === "--") {
    return null;
  }

  // 移除 $ 符号后使用共享的单位转换函数
  const withoutDollar = formattedEPS.replace(/\$/g, "");
  return extractValueWithUnit(withoutDollar);
}

/**
 * 提取 formatNumber 后的纯数值（去掉单位）
 * 用于基于屏幕显示的格式化值计算 Surprise
 * @param value - 要格式化的原始值
 * @returns 格式化后的纯数字（不含 B/K/M 单位）
 */
export function extractFormattedValue(
  value?: string | number | null,
): number | null {
  const numValue = toNumber(value);
  if (numValue == null) {
    return null;
  }
  const formatted = formatNumber(numValue, 2, {
    formatAll: true,
    zeroReturn: "--",
  });
  if (formatted === "--") {
    return null;
  }

  // 使用共享的单位转换函数
  return extractValueWithUnit(formatted);
}

/**
 * 从 revenue 显示字符串中提取纯数字
 * 移除 $、B、M、K 等符号，并根据单位补零还原实际数值
 * @param str - revenue 显示字符串（如 "$152.98M"）
 * @returns 纯数字字符串，无效值返回 "0"
 */
export function extractRevenueNumber(str: string): string {
  if (!str || str === "--") {
    return "0";
  }

  // 移除 $ 符号后使用共享的单位转换函数
  const withoutDollar = str.replace(/\$/g, "");
  const extractedValue = extractValueWithUnit(withoutDollar);

  return extractedValue !== null ? extractedValue.toString() : "0";
}

/**
 * 格式化 revenue 值为显示字符串
 * @param value - 原始 revenue 值
 * @returns 格式化后的显示字符串（如 "$152.98M"）
 */
export function formatRevenueDisplay(value?: string | number | null): string {
  if (value == null) {
    return "--";
  }
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return "--";
  }
  const formatted = formatNumber(numValue, 2, {
    formatAll: true,
    zeroReturn: "--",
  });
  return formatted === "--" ? "--" : `$${formatted}`;
}

/**
 * 判断财报结果
 */
export function determineResult(
  surprise: number | null,
): "BEAT" | "MISSED" | "--" {
  if (surprise === null || surprise === 0) {
    return "--";
  }
  return surprise > 0 ? "BEAT" : "MISSED";
}

/**
 * 格式化财报期间
 * @param quarter - 季度，API 返回 "3"，需要格式化为 "Q3"
 * @param year - 年份，API 返回字符串 "2025"
 */
export function formatFiscalPeriod(
  quarter?: string | number | null,
  year?: string | number | null,
): string {
  const yearNum = toNumber(year);
  if (!yearNum) {
    return "";
  }

  if (quarter != null) {
    const quarterStr = quarter.toString();
    // 如果 quarter 是纯数字（如 "3"），添加 "Q" 前缀
    const formattedQuarter = /^\d+$/.test(quarterStr)
      ? `Q${quarterStr}`
      : quarterStr;

    // Q3 '25 格式
    const shortYear = yearNum.toString().slice(-2);
    return `${formattedQuarter} '${shortYear}`;
  }

  // 2025 格式
  return yearNum.toString();
}

/**
 * 格式化 EPS 值为显示字符串
 * @param value - 可以是字符串、数字或已规范化的数值
 */
export function formatEPSValue(value?: string | number | null): string {
  const numValue = toNumber(value);
  if (numValue == null) {
    return "--";
  }
  // 使用 formatNumber 处理，保留2位小数，自动处理 B/K/M 单位
  const formatted = formatNumber(numValue, 2, {
    formatAll: true,
    zeroReturn: "--",
  });
  return `$${formatted}`;
}

/**
 * 格式化 Surprise 百分比
 * 使用 formatNumber 处理，保留2位小数
 */
export function formatSurprise(surprise: number | null): string {
  if (surprise === null || surprise === 0) {
    return "--";
  }
  // 使用 formatNumber 处理，保留2位小数
  const formatted = formatNumber(Math.abs(surprise), 2, {
    formatAll: true,
    zeroReturn: "--",
  });
  const sign = surprise > 0 ? "+" : "-";
  return `${sign}${formatted}%`;
}

/**
 * 过滤最新非空季度
 * @returns 第一个 eps_actual 和 revenue_actual 都存在的记录
 */
export function filterLatestNonEmptyQuarter(
  earnings?: EarningsCalendar[],
): EarningsCalendar | null {
  if (!earnings?.length) {
    return null;
  }

  return (
    earnings.find(
      (item) => item.eps_actual != null && item.revenue_actual != null,
    ) || null
  );
}

/**
 * 过滤所有非空季度
 * @returns 所有 eps_actual 和 revenue_actual 都存在的记录
 */
export function filterAllNonEmptyQuarters(
  earnings?: EarningsCalendar[],
): EarningsCalendar[] {
  if (!earnings?.length) {
    return [];
  }

  return earnings.filter(
    (item) => item.eps_actual != null && item.revenue_actual != null,
  );
}
